import { useEffect, useRef, useState } from 'react';
import { SubscriptionOwnerContacts } from '../../../domain/use_cases/subscription/getSubscriptionOwnersContacts';

export interface ContactsTransferQuestion {
  ownerNames: string;
  ownersCount: number;
  contactsCount: number;
  /** null when the number is freed: the contacts stay on it or leave with their owner */
  newUserName: string | null;
}

/** `transfer_contacts` of assign-user: true to the new user, false with their owner, undefined left on the number */
export type ContactsTransferAnswer = boolean | undefined;

/** What to ask before a number goes to another user or is freed, null when nobody holds contacts on it */
export const buildContactsTransferQuestion = (
  owners: SubscriptionOwnerContacts[],
  newUserName: string | null,
): ContactsTransferQuestion | null => {
  if (owners.length === 0) return null;

  // A deleted user has no name left
  const ownerNames = owners.map((owner) => `${owner.firstname} ${owner.lastname}`.trim() || 'un compte supprimé').join(', ');
  const contactsCount = owners.reduce((total, owner) => total + owner.contacts_count, 0);

  return { ownerNames, ownersCount: owners.length, contactsCount, newUserName };
};

/** The two buttons of the question: contacts leaving with their owner is the primary one (LEED-2463) */
export const getContactsTransferChoices = (question: ContactsTransferQuestion) => (question.newUserName === null
  ? {
    secondary: { label: 'Garder sur le numéro', answer: undefined },
    primary: { label: `Déplacer avec ${question.ownerNames}`, answer: false },
  }
  : {
    secondary: { label: 'Transférer les contacts', answer: true },
    primary: { label: 'Ne pas transférer', answer: false },
  });

/**
 * Steps a number assignment modal shows in place of its form once confirmed: the contacts transfer
 * question, then a progress message while the backend moves the number and its contacts.
 *
 * Closing the modal cancels the running assignment, whatever its step: the question it waits for, or is
 * about to ask, resolves null and the caller stops before writing anything.
 */
export const useNumberAssignmentSteps = () => {
  const [transferQuestion, setTransferQuestion] = useState<ContactsTransferQuestion | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const resolveTransfer = useRef<((transfer: ContactsTransferAnswer | null) => void) | null>(null);
  // Bumped by each assignment and each cancellation: an assignment whose number is no longer the last one was cancelled
  const assignmentRef = useRef(0);

  // A new assignment starts: returns its number, to hand to askContactsTransfer
  const startAssignment = () => ++assignmentRef.current;

  // Resolves with the answer, undefined at once when nobody holds contacts on the number, null when the
  // assignment was cancelled
  const askContactsTransfer = (
    iAssignment: number,
    owners: SubscriptionOwnerContacts[],
    newUserName: string | null,
  ): Promise<ContactsTransferAnswer | null> => {
    if (iAssignment !== assignmentRef.current) return Promise.resolve(null);
    const question = buildContactsTransferQuestion(owners, newUserName);
    if (!question) return Promise.resolve(undefined);

    setTransferQuestion(question);
    return new Promise((resolve) => {
      resolveTransfer.current = resolve;
    });
  };

  const answerContactsTransfer = (transfer: ContactsTransferAnswer) => {
    setTransferQuestion(null);
    resolveTransfer.current?.(transfer);
    resolveTransfer.current = null;
  };

  // The modal is closed: the pending question is dropped and the running assignment stops before writing
  const cancelAssignment = () => {
    assignmentRef.current += 1;
    setTransferQuestion(null);
    resolveTransfer.current?.(null);
    resolveTransfer.current = null;
  };

  // Leaving the page cancels too: no question survives it
  useEffect(() => () => {
    assignmentRef.current += 1;
    resolveTransfer.current?.(null);
  }, []);

  return { transferQuestion, isAssigning, setIsAssigning, startAssignment, askContactsTransfer, answerContactsTransfer, cancelAssignment };
};